import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../contexts/AuthContext';
import { loginSchema } from '../../schemas/authSchema';
import { Link, useNavigate } from 'react-router-dom';
import PasswordChangeModal from '../PasswordChangeModal';

interface LoginFormData {
  identifier: string;
  password: string;
}

const LoginForm: React.FC = () => {
  const { login, user, changePassword } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  useEffect(() => {
    if (user && !showPasswordChangeModal) {
      navigate('/form');
    }
  }, [user, navigate, showPasswordChangeModal]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setError('');

    try {
      const result = await login(data.identifier, data.password);
      
      if (result.mustChangePassword) {
        setIsFirstLogin(user?.isFirstLogin || false);
        setShowPasswordChangeModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Pålogging feilet. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChangeSuccess = (updatedUser: any) => {
    setShowPasswordChangeModal(false);
    setIsFirstLogin(false);
    // Navigation will happen via useEffect when user state updates
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <div className="auth-header">
          <h2>Logg inn til PMsalg skjema</h2>
          <p>Tilgang til ditt sikre salgsskjema</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}
          
          <div className="auth-form-group">
            <label htmlFor="identifier" className="sr-only">
              Brukernavn eller e-post
            </label>
            <input
              {...register('identifier')}
              type="text"
              className="first-input"
              placeholder="Brukernavn eller e-post"
            />
            {errors.identifier && (
              <p className="error-message">{errors.identifier.message}</p>
            )}
          </div>
          
          <div className="auth-form-group">
            <label htmlFor="password" className="sr-only">
              Passord
            </label>
            <input
              {...register('password')}
              type="password"
              className="last-input"
              placeholder="Passord"
            />
            {errors.password && (
              <p className="error-message">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="auth-submit"
          >
            {isSubmitting && <span className="spinner"></span>}
            {isSubmitting ? 'Logger inn...' : 'Logg inn'}
          </button>

          <div className="auth-link">
            <Link to="/forgot-password">
              Glemt passord?
            </Link>
          </div>
        </form>
      </div>

      {/* Password Change Modal for First Login */}
      <PasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={() => {}} // Cannot close on first login
        onSuccess={handlePasswordChangeSuccess}
        isFirstLogin={isFirstLogin}
      />
    </div>
  );
};

export default LoginForm;